import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { ErrorState, SkeletonList, ToggleRow } from "@teamna/ui/components/index.ts";

/**
 * One Control Center switch of one business, in its own card: a heading and one `ToggleRow`, with
 * the loading and error states. The "Runs on Teamna" link uses it.
 */
export function SwitchCard<T>({
  id,
  title,
  loadingLabel,
  state,
  pending,
  row,
  onChange,
}: {
  /** The id of the heading, unique on the page. */
  id: string;
  title: string;
  loadingLabel: string;
  state: UseQueryResult<T>;
  pending: boolean;
  /** The row for the value that the server sent. */
  row: (data: T) => { label: string; hint: string; checked: boolean; disabled?: boolean };
  onChange: (on: boolean) => void;
}) {
  let body: ReactNode;
  if (state.data) {
    const { label, hint, checked, disabled } = row(state.data);
    body = <ToggleRow label={label} hint={hint} checked={checked} disabled={disabled || pending} onChange={onChange} />;
  } else if (state.isLoading) {
    body = (
      <output aria-label={loadingLabel} className="block">
        <SkeletonList rows={1} />
      </output>
    );
  } else {
    body = <ErrorState error={state.error} onRetry={() => void state.refetch()} />;
  }

  return (
    <section aria-labelledby={id} className="mb-8 rounded-card border border-line bg-paper px-5 pt-5 pb-2 shadow-card">
      <h2 id={id} className="text-[17px]">
        {title}
      </h2>
      {body}
    </section>
  );
}
